import React, { createContext, useContext } from "react";

import { api } from "EmployerApplication/urls";
import { intuitDocsDescription } from "Intuit/utils";
import { primepayDocsDescription } from "Primepay/utils";
import { remoteDocsDescription } from "Remote/utils";
import { squareDocsDescription } from "Square/utils";
import companyToken from "Utils/companyToken";
import fetchWithCSRFToken, { fetchWithCSRFTokenNoContentType } from "Utils/fetchWithCSRFToken";
import makeResponsiveBem from "Utils/makeResponsiveBem";

import BodyText from "Common/BodyText";
import { usePartnerContext } from "Common/PartnerContext";
import { LinkButton } from "Theme/Button";

import makeBem from "Utils/makeBem";

export const DocumentPickerContext = createContext();
export const useDocumentPickerContext = () => useContext(DocumentPickerContext);

export const bem = makeBem("DocumentPicker");
export const responsiveBem = makeResponsiveBem("DocumentPicker");

// The fileInputName is a string that starts with a docType followed
// by a docKey in square brackets, i.e.: doc_type_name[docKey].
// The docType is in lowercase with underscore.
// More info in app/interactors/new_group/list_verification_documents.rb
export const parseDocType = (fileInputName) => fileInputName.substring(0, fileInputName.indexOf("["));

export const parseDocKey = (fileInputName) => fileInputName.substring(fileInputName.indexOf("[") + 1, fileInputName.indexOf("]"));

// formats the document type into the copy that Product expects to
// find in analytics events
export const docTypeForAnalytics = (fileInputName) => {
  const docType = parseDocType(fileInputName);
  if (docType === "state_wage_report") {
    return "wage_document";
  }
  if (docType === "ownership_tax") {
    return "tax_document";
  }
  return docType;
};

export const convertFileToFormData = (file, fileName, isAlternate) => {
  const form = new FormData();
  form.append(fileName, file);
  form.append("isAlternate", isAlternate);
  return form;
};

const StateWageReportReceiptDescription = () => (
  <BodyText>
    When you electronically file your quarterly wage report, you should receive a form from the state. If you don&rsquo;t have this document you can download it{" "}
    <LinkButton color="link" style={{ verticalAlign: "inherit" }} p={0} href="https://secureaccess.wa.gov/esd/eams/filetwr/taxsummary.aspx" target="_blank">
      here
    </LinkButton>
    .
  </BodyText>
);

export interface UseModalDescriptionProps {
  fileInputName: string;
  description: string;
  title: string;
}
const useStateSpecificDocsDescription = ({ fileInputName }: UseModalDescriptionProps) => {
  const { meta } = useDocumentPickerContext();
  const docType = parseDocType(fileInputName);
  const { state } = meta || {};
  if (docType === "quarterly_wage_report_form" && state === "WA") {
    return <StateWageReportReceiptDescription />;
  }

  // Implement state-specific logic here
  return null;
};

export const useModalDescription = (descriptionProps: UseModalDescriptionProps) => {
  const { partner } = usePartnerContext();
  const stateSpecificModal = useStateSpecificDocsDescription(descriptionProps);

  let partnerSpecificModal;
  switch (partner) {
    case "intuit":
      partnerSpecificModal = intuitDocsDescription(descriptionProps);
      break;
    case "square":
      partnerSpecificModal = squareDocsDescription(descriptionProps);
      break;
    case "primepay":
      partnerSpecificModal = primepayDocsDescription(descriptionProps);
      break;
    case "remote":
      partnerSpecificModal = remoteDocsDescription(descriptionProps);
      break;
    default:
      partnerSpecificModal = null;
      break;
  }
  if (partnerSpecificModal) {
    return partnerSpecificModal;
  }

  if (stateSpecificModal) {
    return stateSpecificModal;
  }

  return descriptionProps.description;
};

export const explanationNotSet = (noFileExplanation) => !noFileExplanation || (noFileExplanation && noFileExplanation.length === 0);

// Appends the document picker index to the name of a modal so we can open the
// right one for the document we're interested in. Otherwise, each document picker
// has modals with the same names.
export const getModalName = (modalName, index) => `${modalName}-${index}`;

export const documentUpload = (form, companyPolicyId) => {
  const companyQueryString = companyToken() ? `?company_id_token=${companyToken()}` : "";
  return fetchWithCSRFTokenNoContentType(`${api.documentsUpload}/${companyPolicyId}${companyQueryString}`, {
    method: "POST",
    body: form,
    headers: {
      Accept: "application/json",
    },
  });
};

export const wageDocumentUpload = (form, companyPolicyId) => {
  const companyQueryString = companyToken() ? `?company_id_token=${companyToken()}` : "";
  return fetchWithCSRFTokenNoContentType(`${api.wageDocumentsUpload}/${companyPolicyId}${companyQueryString}`, {
    method: "POST",
    body: form,
    headers: {
      Accept: "application/json",
    },
  });
};

export const validateEmployerApplicationDocument = (document, companyPolicyId) => {
  const companyQueryString = companyToken() ? `?company_id_token=${companyToken()}` : "";
  return fetchWithCSRFToken(`${api.employerApplicationDocumentValidate}/${companyPolicyId}${companyQueryString}`, {
    method: "POST",
    body: JSON.stringify(document),
  });
};
